// EmailSendHistory — shows all email send records for an entity (invoices, statements, reports, deliverables)
import React, { useState } from 'react';
import { Mail, Download, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import EmlViewerDialog from './EmlViewerDialog';
import { fmtDatetime } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

const downloadEml = (fileId, filename) => {
  const token = localStorage.getItem('token');
  fetch(`${window.location.origin}/api/files/download/${fileId}`, {
    headers: { Authorization: `Bearer ${token}` }
  }).then(r => r.blob()).then(blob => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'email.eml';
    a.click();
    URL.revokeObjectURL(url);
  });
};

/**
 * EmailSendHistory
 * @param {Array}    sends       - array of {sent_at, sent_by, sent_to, eml_file_id}
 * @param {Function} onResend    - called when Re-send Email is clicked
 * @param {string}   emlFilename - download filename for .eml
 * @param {string}   layout      - "inline" (default) or "stacked"
 */
const EmailSendHistory = ({ sends = [], onResend, emlFilename = 'email.eml', layout = 'inline' }) => {
  const { userTimezone } = useAuth();
  const [viewFileId, setViewFileId] = useState(null);
  const [viewSentAt, setViewSentAt] = useState(null);

  if (!sends || sends.length === 0) return null;

  const sorted = [...sends].sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));

  return (
    <>
      <div className="space-y-1.5">
        {sorted.map((send, idx) => (
          layout === 'stacked' ? (
            /* Stacked layout: badge on top, actions below (for narrow contexts like PostSaleTab) */
            <div key={idx} className="space-y-1">
              <div className="flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 bg-green-50 border-green-200">
                <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-green-700 font-medium text-xs">
                  {idx === 0 ? 'Emailed' : 'Re-sent'}
                  {send.sent_to ? ` to ${send.sent_to}` : ''}
                  {send.sent_at ? ` on ${fmtDatetime(send.sent_at, userTimezone)}` : ''}
                  {send.sent_by ? ` by ${send.sent_by}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-2 pl-1">
                {send.eml_file_id && (
                  <>
                    <button className="text-muted-foreground hover:text-primary" aria-label="View sent email"
                      onClick={() => { setViewFileId(send.eml_file_id); setViewSentAt(send.sent_at); }}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="text-muted-foreground hover:text-primary" aria-label="Download .eml"
                      onClick={() => downloadEml(send.eml_file_id, emlFilename)}>
                      <Download className="w-4 h-4" />
                    </button>
                  </>
                )}
                {idx === 0 && onResend && (
                  <Button size="sm" variant="outline" onClick={onResend}>
                    <Mail className="w-4 h-4 mr-2" /> Re-send Email
                  </Button>
                )}
              </div>
            </div>
          ) : (
            /* Inline layout: badge + icons + resend all on one row (default, for wider dialogs) */
            <div key={idx} className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 bg-green-50 border-green-200 flex-1 min-w-0">
                <Mail className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-green-700 font-medium text-xs truncate">
                  {idx === 0 ? 'Emailed' : 'Re-sent'}
                  {send.sent_to ? ` to ${send.sent_to}` : ''}
                  {send.sent_at ? ` on ${fmtDatetime(send.sent_at, userTimezone)}` : ''}
                  {send.sent_by ? ` by ${send.sent_by}` : ''}
                </span>
              </div>
              {send.eml_file_id && (
                <>
                  <button className="text-muted-foreground hover:text-primary" aria-label="View sent email"
                    onClick={() => { setViewFileId(send.eml_file_id); setViewSentAt(send.sent_at); }}>
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="text-muted-foreground hover:text-primary" aria-label="Download .eml"
                    onClick={() => downloadEml(send.eml_file_id, emlFilename)}>
                    <Download className="w-4 h-4" />
                  </button>
                </>
              )}
              {idx === 0 && onResend && (
                <Button size="sm" variant="outline" onClick={onResend}>
                  <Mail className="w-4 h-4 mr-2" /> Re-send Email
                </Button>
              )}
            </div>
          )
        ))}
      </div>

      <EmlViewerDialog
        open={!!viewFileId}
        onClose={() => { setViewFileId(null); setViewSentAt(null); }}
        fileId={viewFileId}
        sentAt={viewSentAt}
        title="Sent Email Preview"
      />
    </>
  );
};

export default EmailSendHistory;

